import { queryOptions } from '@tanstack/react-query';
import * as friendshipApi from '../endpoints/friendship';
import { Friend, FriendRequest, FriendshipStatusResponse, SuggestedFriend } from '@/types/friendship';

/**
 * Query keys for friendships
 */
export const FRIENDS_QUERY_KEYS = {
  all: ['friends'] as const,
  list: () => [...FRIENDS_QUERY_KEYS.all, 'list'] as const,
  user: (userId: string) => [...FRIENDS_QUERY_KEYS.all, 'user', userId] as const,
  requests: () => [...FRIENDS_QUERY_KEYS.all, 'requests'] as const,
  sentRequests: () => [...FRIENDS_QUERY_KEYS.all, 'sent-requests'] as const,
  status: (userId: string) => [...FRIENDS_QUERY_KEYS.all, 'status', userId] as const,
  suggested: () => [...FRIENDS_QUERY_KEYS.all, 'suggested'] as const,
} as const;

/**
 * Domain-oriented query options for friendship operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for listing friends
 *
 * @param params - Query parameters for pagination
 * @returns Query options for the friends list
 *
 * @example
 * ```typescript
 * const options = friendsQueryOptions();
 * const optionsWithPagination = friendsQueryOptions({ page: 2, per_page: 20 });
 * ```
 */
export const friendsQueryOptions = (
  params: friendshipApi.ListFriendsParams = {}
) =>
  queryOptions({
    queryKey: FRIENDS_QUERY_KEYS.list(),
    queryFn: async (): Promise<Friend[]> => {
      return friendshipApi.getFriends(params);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching incoming friend requests
 *
 * @param params - Query parameters for pagination
 * @returns Query options for incoming friend requests
 *
 * @example
 * ```typescript
 * const options = friendRequestsQueryOptions();
 * ```
 */
export const friendRequestsQueryOptions = (
  params: friendshipApi.ListFriendRequestsParams = {}
) =>
  queryOptions({
    queryKey: FRIENDS_QUERY_KEYS.requests(),
    queryFn: async (): Promise<FriendRequest[]> => {
      return friendshipApi.getFriendRequests(params);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes (shorter for requests)
    gcTime: 1000 * 60 * 10, // 10 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching sent friend requests
 *
 * @param params - Query parameters for pagination
 * @returns Query options for sent friend requests
 *
 * @example
 * ```typescript
 * const options = sentFriendRequestsQueryOptions();
 * ```
 */
export const sentFriendRequestsQueryOptions = (
  params: friendshipApi.ListFriendRequestsParams = {}
) =>
  queryOptions({
    queryKey: FRIENDS_QUERY_KEYS.sentRequests(),
    queryFn: async (): Promise<FriendRequest[]> => {
      return friendshipApi.getSentRequests(params);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes (shorter for requests)
    gcTime: 1000 * 60 * 10, // 10 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for checking friendship status with a user
 *
 * @param userId - The user ID to check friendship status with
 * @returns Query options for friendship status
 *
 * @example
 * ```typescript
 * const options = friendshipStatusQueryOptions('user123');
 * ```
 */
export const friendshipStatusQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: FRIENDS_QUERY_KEYS.status(userId),
    queryFn: async ({ signal }): Promise<FriendshipStatusResponse> => {
      return friendshipApi.getFriendshipStatus(userId, signal);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching a specific user's friends list
 *
 * @param userId - The user ID to fetch friends for
 * @returns Query options for the user's friends list
 */
export const userFriendsQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: FRIENDS_QUERY_KEYS.user(userId),
    queryFn: async (): Promise<Friend[]> => {
      return friendshipApi.getUserFriends(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching suggested friends
 *
 * @param params - Optional limit parameter
 * @returns Query options for suggested friends
 */
export const suggestedFriendsQueryOptions = (
  params: { limit?: number } = {}
) =>
  queryOptions({
    queryKey: FRIENDS_QUERY_KEYS.suggested(),
    queryFn: async (): Promise<SuggestedFriend[]> => {
      return friendshipApi.getSuggestedFriends(params);
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: {
      persist: false,
    },
  });
